import includes from 'lodash-es/includes'
import min from 'lodash-es/min'
import some from 'lodash-es/some'
import uniq from 'lodash-es/uniq'
import without from 'lodash-es/without'

import type { Offer } from '@fourthwall/services/dist/models/offer'

import { YOU_FULFILL } from './Product.consts'
import type { ListingType, OfferFulfillmentType, OfferType } from './Product.types'

export const isDigitalFulfillment = (fulfillmentService: Offer.FulfillingService) =>
  fulfillmentService === 'DIGITAL_ITEM'

const isScreenPrintedFulfillment = (fulfillmentService: Offer.FulfillingService) =>
  fulfillmentService === 'WLM' || fulfillmentService === 'DUBOW'

const isFulfilledByCreator = (fulfillmentService: Offer.FulfillingService) =>
  fulfillmentService === 'FULFILLED_BY_CREATOR'

export const getFulfillmentType = (
  fulfillmentService: Offer.FulfillingService,
): OfferFulfillmentType => {
  if (isDigitalFulfillment(fulfillmentService)) {
    return 'DIGITAL'
  }
  if (isScreenPrintedFulfillment(fulfillmentService)) {
    return 'SCREEN_PRINTED'
  }
  if (isFulfilledByCreator(fulfillmentService)) {
    return 'FULFILLED_BY_CREATOR'
  }
  if (fulfillmentService === 'SHIP_MONK') {
    return 'SHIP_MONK'
  }
  if (fulfillmentService === 'CART_JAZZ') {
    return 'CART_JAZZ'
  }

  return 'ON_DEMAND'
}

export const isFourthwallFulfilled = (fulfillmentType: OfferFulfillmentType): boolean => {
  return fulfillmentType === 'ON_DEMAND' || fulfillmentType === 'SCREEN_PRINTED'
}

export const isShipmentStartDateEditable = (
  manufacturingService: Offer.ManufacturingService,
): boolean => {
  return [
    'MANUFACTURED_BY_CREATOR',
    'FOURTHWALL_BESPOKE',
    'DUBOW',
    'OVERCAST',
    'P3',
    'REDWALL',
  ].includes(manufacturingService)
}

export const getOfferTypeUtil = (manufacturingService: Offer.ManufacturingService) => {
  const offerTypeMap: Record<typeof manufacturingService, OfferType> = {
    DEMO_PRODUCT: 'ON_DEMAND',
    DIGITAL_ITEM: 'DIGITAL',
    DUBOW: 'MOQ',
    DUBOW_DTFX: 'ON_DEMAND',
    FOURTHWALL_BESPOKE: 'MANUAL',
    MANUFACTURED_BY_CREATOR: 'MANUAL',
    MWW: 'ON_DEMAND',
    OPTIGER: 'ON_DEMAND',
    OVERCAST: 'MOQ',
    P3: 'MOQ',
    REDWALL: 'MOQ',
    PRINTFUL: 'ON_DEMAND',
    SPOKE: 'ON_DEMAND',
    TRIBE: 'MANUAL',
    WLM: 'MOQ',
    KNITWISE: 'ON_DEMAND',
    DOD: 'ON_DEMAND',
    COMBINED: 'ON_DEMAND',
    BUNDLE: 'ON_DEMAND',
    STAKES: 'ON_DEMAND',
  }

  return offerTypeMap[manufacturingService]
}

export const YOU_MANU: Offer.ManufacturingService[] = ['MANUFACTURED_BY_CREATOR']

export const getListingTypeUtil = (state: Offer.Item | undefined): ListingType | undefined => {
  // NOTE: https://popshop.atlassian.net/browse/SLA-667
  if (!state) {
    return undefined
  }

  const { manufacturingService, fulfillingService } = state

  if (YOU_MANU.includes(manufacturingService) && YOU_FULFILL.includes(fulfillingService)) {
    return 'YOU'
  }

  const ON_DEMAND_MANU: Offer.ManufacturingService[] = ['PRINTFUL', 'SPOKE']

  if (ON_DEMAND_MANU.includes(manufacturingService)) {
    return 'ON_DEMAND'
  }

  const MOQ_MANU: Offer.ManufacturingService[] = ['WLM', 'P3', 'DUBOW', 'OVERCAST', 'REDWALL']

  if (MOQ_MANU.includes(manufacturingService)) {
    return 'MOQ'
  }

  const DIGITAL_MANU: Offer.ManufacturingService[] = ['DIGITAL_ITEM']

  if (DIGITAL_MANU.includes(manufacturingService)) {
    return 'DIGITAL'
  }

  // We handle shipping
  return 'WE'
}

export const getCostsPerSize = (variants: Offer.Item['variants']) => {
  return variants.reduce<Record<string, (number | null)[]>>((result, currentVariant) => {
    const size = currentVariant.size

    if (!size) return result

    const cost = currentVariant.cost?.value || null

    if (size in result) {
      if (includes(result[size], cost)) {
        return result
      }

      return {
        ...result,
        [size]: [...result[size], cost],
      }
    }

    return {
      ...result,
      [size]: [cost],
    }
  }, {})
}

export const getSameSizesHaveDifferentCosts = (
  costsPerSizeResult: ReturnType<typeof getCostsPerSize>,
) => {
  return some(costsPerSizeResult, (size: (number | null)[]) => size.length > 1)
}

export const getCosts = (variants: Offer.Item['variants']) => {
  return variants.reduce<(number | null)[]>((result, currentVariant) => {
    const cost = currentVariant.cost?.value || null

    return [...result, cost]
  }, [])
}

export const getSizesWithHigherCosts = (
  variants: Offer.Item['variants'],
  costs: ReturnType<typeof getCosts>,
) => {
  const lowestCost = min(without(costs, null))

  return variants.reduce<string[]>((result, currentVariant) => {
    const size = currentVariant.size
    const cost = currentVariant.cost?.value

    if (lowestCost && cost && cost > lowestCost && size && !includes(result, size)) {
      return [...result, size]
    }

    return result
  }, [])
}

export const getSizesPerCost = (
  costs: ReturnType<typeof getCosts>,
  costsPerSize: ReturnType<typeof getCostsPerSize>,
) => {
  return (
    costs &&
    uniq(costs).map((cost) => {
      const sizes =
        (costsPerSize &&
          Object.entries(costsPerSize).reduce((result, [key, value]) => {
            return value.includes(cost) && !result.includes(key) ? [...result, key] : result
          }, [] as string[])) ||
        []

      return { cost, sizes }
    })
  )
}
